import { Divider, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  prompt: string;
  product: string;
  region: string;
  regionSplitInstructions: string;
  subsearchInstructions: string;
  peopleSearchInstructions: string;
  isGenerating: boolean;

  handlePromptChange(event: React.ChangeEvent<HTMLInputElement>): void;

  setProduct(product: string): void;

  setRegion(region: string): void;

  setRegionSplitInstructions(regionSplitInstructions: string): void;

  setSubsearchInstructions(subsearchInstructions: string): void;

  setPeopleSearchInstructions(peopleSearchInstructions: string): void;

  handleGenerateLeadList(): void;
}

const LeadListFormCreation: React.FC<Props> = ({
  prompt,
  product,
  region,
  regionSplitInstructions,
  subsearchInstructions,
  peopleSearchInstructions,
  isGenerating,
  handlePromptChange,
  setProduct,
  setRegion,
  setRegionSplitInstructions,
  setSubsearchInstructions,
  setPeopleSearchInstructions,
  handleGenerateLeadList,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div>
        <Typography className={classes.subTitle} mb={1}>
          {t('enterYourPrompt')}
        </Typography>
        <Typography className={classes.description}>{t('leadListPromptDescription')}</Typography>
      </div>
      <PaiTextArea placeholder={t('overallPromptPlaceholder')} variant={'background'} value={prompt} onChange={handlePromptChange} />
      <Divider sx={{ margin: '8px 0' }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
        <PaiTextField
          label={t('productOrIndustry')}
          placeholder={t('productOrIndustryPlaceholder')}
          variant={'background'}
          value={product}
          onChange={(e) => setProduct(e.target.value)}
        />
        <PaiTextField
          label={t('regionOrLocation')}
          placeholder={t('regionOrLocationPlaceholder')}
          variant={'background'}
          value={region}
          onChange={(e) => setRegion(e.target.value)}
        />
      </Stack>
      <Divider sx={{ margin: '8px 0' }} />
      <PaiTextField
        label={t('regionSplitInstructions')}
        placeholder={t('regionSplitInstructionsPlaceholder')}
        variant={'background'}
        value={regionSplitInstructions}
        onChange={(e) => setRegionSplitInstructions(e.target.value)}
      />
      <PaiTextField
        label={t('companyIdentificationInstructions')}
        placeholder={t('companyIdentificationInstructionsPlaceholder')}
        variant={'background'}
        value={subsearchInstructions}
        onChange={(e) => setSubsearchInstructions(e.target.value)}
      />
      <PaiTextField
        label={t('peopleSearchInstructions')}
        placeholder={t('peopleSearchInstructionsPlaceholder')}
        variant={'background'}
        value={peopleSearchInstructions}
        onChange={(e) => setPeopleSearchInstructions(e.target.value)}
      />
      <Divider sx={{ margin: '8px 0' }} />
      <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
        <PaiButton
          text={isGenerating ? t('generating') : t('generateLeadList')}
          border={'rounded'}
          action={handleGenerateLeadList}
          disabled={!prompt.trim() && (!product.trim() || !region.trim())}
          loading={isGenerating}
        />
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  description: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
}));

export default LeadListFormCreation;
